import { avatarPlaceholderUrl } from '@/utils/avatarPlaceholder.js';
import { flavourOf } from '@/components/Player/Creation/pathFlavour.js';
import { mediaUrl } from '@/components/Player/mediaUrl.js';

/*
 * The picker's own selection rules, kept out of PortraitPicker.vue because
 * `<script setup>` exposes neither `.computed` nor `.methods` to a
 * hand-built `this` — src/tests/component/'s usual technique does not reach
 * this component at all. Everything a test needs to pin lives here instead,
 * where it is a plain function.
 *
 * The API (Task 2) filters by (path, gender) server-side and returns no path
 * on the row itself, so "filtering a preset list by path and gender" is not
 * this module's job — the fetch already did that. What this module owns is
 * the one thing the fetch cannot: the always-present first tile (the
 * placeholder the server would assign with nothing chosen) and the rule for
 * when a held selection stops being valid.
 */

/* `null` doubles as both "nothing chosen yet" and "the placeholder was
   chosen on purpose" — the same value `character.bio.avatar_preset` carries
   to the backend for either case (apps/character/api/serializers/openapi.py,
   Task 3). A dedicated sentinel would need translating at the API boundary
   for no benefit. */
export const DEFAULT_PRESET_ID = null;

export function defaultPresetTile(path, gender) {
  return {
    id: DEFAULT_PRESET_ID,
    name: '',
    image: avatarPlaceholderUrl(gender, path ? flavourOf(path) : undefined),
    school: null,
    sort_order: -1,
  };
}

export function presetTiles(presets, path, gender) {
  const list = Array.isArray(presets) ? presets : [];
  return [defaultPresetTile(path, gender), ...list];
}

export function defaultSelectionFor() {
  return DEFAULT_PRESET_ID;
}

/*
 * The reset rule. A selection made against one (path, gender) pair is a
 * preset id that may not exist in the list fetched for a different pair —
 * the player went back to Identity and changed gender, or back further and
 * changed path. Silently keeping a foreign id would submit a preset the
 * validation in Task 3 rejects anyway, just later and less legibly. The
 * default id is always valid, since it is manufactured fresh for whatever
 * (path, gender) is asked about.
 */
export function resolveSelection(presets, selectedId) {
  const list = Array.isArray(presets) ? presets : [];
  const stillValid =
    selectedId === DEFAULT_PRESET_ID || list.some((preset) => preset.id === selectedId);
  return stillValid ? selectedId : DEFAULT_PRESET_ID;
}

/*
 * The Oath screen's own use of the same catalogue: resolve a chosen preset id
 * back to the image it names, or the placeholder the server would install
 * with nothing chosen. Kept beside the other selection rules rather than
 * inline in CharacterOath.vue for the same testability reason as above.
 */
export function portraitFor(presets, selectedId, path, gender) {
  const list = Array.isArray(presets) ? presets : [];
  const chosen = list.find((preset) => preset.id === selectedId);
  if (chosen) return mediaUrl(chosen.image);
  return avatarPlaceholderUrl(gender, path ? flavourOf(path) : undefined);
}
